/**
 * Decisões de apresentação da tela de Contas Bancárias que valem a pena existir
 * fora do componente: as duas nasceram de defeito silencioso, e defeito
 * silencioso só não volta quando há teste olhando.
 *
 * - `agruparContasPorPai` já apagou conta da tela (neto e ciclo).
 * - `filaDoAssistente` era aritmética de índice, e pulava uma transação a cada
 *   vínculo no assistente de conciliação.
 *
 * Ambas são puras de propósito: recebem a lista, devolvem a lista. Nada de
 * estado, requisição ou React aqui.
 */

export type ContaComPai = {
  id: number;
  /** FK para a conta corrente que alimenta esta (bank_accounts.parentAccountId). */
  parentAccountId?: number | string | null;
};

export type GrupoDeContas<T> = {
  /** A conta que vira card na grade. */
  acc: T;
  /** Aplicações penduradas nela. Vazio quando não há. */
  filhas: T[];
};

/**
 * Agrupa aplicação financeira dentro da conta corrente que a alimenta.
 *
 * A regra que sustenta tudo: **toda conta aparece exatamente uma vez**, como
 * card próprio ou como filha. Preferir "fora do lugar" a "invisível" não é
 * gosto — conta que some da grade continua contada no "Saldo total — N contas"
 * do topo, então o operador não tem como notar a ausência.
 *
 * Por isso a conta só é aninhada quando o pai dela é raiz, ou seja, quando o pai
 * é um card de verdade na tela. Fica de fora:
 *
 * - **neto** (A←B←C): B é desenhado como filha, e filha não desenha filha;
 * - **ciclo** (A↔B, ou de três): não sobraria raiz nenhuma;
 * - **pai inexistente, inativo ou auto-referência**: não há em quem pendurar.
 *
 * Nos três casos a conta vira card próprio. Sem recursão, então ciclo não trava.
 *
 * Pela tela nada disso acontece — o select de conta-pai só oferece contas
 * não-investimento, e só aparece para conta de investimento, o que limita a
 * hierarquia a dois níveis. Mas `parentAccountId` é gravável por SQL e pela API.
 */
export function agruparContasPorPai<T extends ContaComPai>(accounts: T[]): GrupoDeContas<T>[] {
  const byId = new Map<number, T>(accounts.map(a => [a.id, a]));

  // Pai que existe NESTA lista e não é a própria conta.
  const paiDe = (acc: T): T | undefined => {
    if (acc.parentAccountId == null) return undefined;
    const pai = byId.get(Number(acc.parentAccountId));
    return !pai || pai.id === acc.id ? undefined : pai;
  };
  const ehRaiz = (acc: T) => paiDe(acc) === undefined;

  const filhasPorPai = new Map<number, T[]>();
  const raizes: T[] = [];
  for (const acc of accounts) {
    const pai = paiDe(acc);
    if (pai && ehRaiz(pai)) {
      filhasPorPai.set(pai.id, [...(filhasPorPai.get(pai.id) || []), acc]);
    } else {
      raizes.push(acc);
    }
  }
  return raizes.map(acc => ({ acc, filhas: filhasPorPai.get(acc.id) || [] }));
}

export type TransacaoDaFila = {
  id: number;
  isReconciled?: boolean;
};

export type FilaDoAssistente<T> = {
  /** O que ainda não passou pelas mãos do operador, na ordem da tela. */
  fila: T[];
  /** A transação da vez. `undefined` quando a fila acabou. */
  atual: T | undefined;
  /** Denominador do "X de N". Não encolhe no meio da rodada. */
  total: number;
};

/**
 * Fila do assistente de conciliação, por id.
 *
 * O assistente guardava a posição do operador como índice dentro da lista de
 * não conciliadas — uma lista que encolhe. Ao vincular a transação da posição i,
 * o refetch a tirava da lista, o índice ia para i+1, e a que tinha escorregado
 * para i não aparecia para ninguém. Numa fila de dez vínculos, metade passava
 * batida, e passar batida aqui é dinheiro do extrato sem documento do outro lado.
 *
 * `tratadas` guarda quem já foi vinculada ou pulada nesta rodada. A transação da
 * vez é sempre a primeira que sobrou, então não há aritmética que possa pular.
 *
 * `total` é `tratadas + restantes`: vincular move uma transação de um lado para
 * o outro, e a soma não muda no meio da rodada. Com o índice, o total encolhia
 * junto com a lista e o contador andava para trás.
 */
export function filaDoAssistente<T extends TransacaoDaFila>(
  transactions: T[],
  tratadas: Set<number>,
): FilaDoAssistente<T> {
  const pendentes = transactions.filter(tx => !tx.isReconciled);
  const fila = pendentes.filter(tx => !tratadas.has(tx.id));
  return { fila, atual: fila[0], total: tratadas.size + fila.length };
}
